import ConfirmAction from '@/components/confirm-action';
import PageHeader from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate, formatDateTime, formatMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import InvoiceStatusPill from '@/pages/apps/components/invoice-status-pill';
import MessageTable from '@/pages/apps/components/message-table';
import BooksCard from '@/pages/apps/invoices/components/books-card';
import PayLinkCard from '@/pages/apps/invoices/components/pay-link-card';
import {
  app_connections_path,
  app_invoice_delivery_path,
  app_invoice_voidance_path,
  edit_app_list_record_path,
} from '@/rails/routes';
import type { AppInvoice, AppShell, Connection, Message, ProviderMode } from '@/types/platform';
import { Link, router } from '@inertiajs/react';
import { useState, type ReactNode } from 'react';

interface SourceRecord {
  id: string;
  title: string;
  listKey: string;
}

interface InvoicesShowProps {
  app: AppShell;
  invoice: AppInvoice;
  record: SourceRecord | null;
  messages: Message[];
  paymentConnection: Connection | null;
  booksConnection: Connection | null;
  mailMode: ProviderMode;
}

interface DetailProps {
  label: string;
  children: ReactNode;
  className?: string;
}

const Detail = ({ label, children, className }: DetailProps) => {
  return (
    <div className={cn('min-w-0', className)}>
      <dt className="text-muted-foreground text-xs font-medium tracking-wide uppercase">{label}</dt>
      <dd className="mt-1 text-sm break-words">{children}</dd>
    </div>
  );
};

const InvoicesShow = ({
  app,
  invoice,
  record,
  messages,
  paymentConnection,
  booksConnection,
  mailMode,
}: InvoicesShowProps) => {
  const [sending, setSending] = useState(false);
  const canSend = invoice.status === 'draft' || invoice.status === 'open';
  const sendLabel = invoice.sentAt ? 'Send again' : 'Send to customer';
  const recipient = invoice.customer?.email ?? 'the customer';
  // The confirmation says who is emailed. In demo mode nothing leaves the app.
  const emailNote =
    mailMode === 'demo'
      ? `Demo mode: the email to ${recipient} is recorded here but not sent.`
      : `This emails the invoice to ${recipient}.`;

  return (
    <div className="space-y-6">
      <PageHeader
        title={invoice.number ?? 'Draft invoice'}
        description={invoice.customer ? `For ${invoice.customer.name}` : undefined}
        badges={<InvoiceStatusPill status={invoice.status} />}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <Card>
            <CardContent className="space-y-6">
              <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <Detail label="Bill to">
                  {invoice.customer ? (
                    <>
                      <span className="font-medium">{invoice.customer.name}</span>
                      <br />
                      <span className="text-muted-foreground">{invoice.customer.email}</span>
                    </>
                  ) : (
                    '—'
                  )}
                </Detail>
                <Detail label="Due">
                  {invoice.dueOn ? formatDate(invoice.dueOn) : 'No due date'}
                </Detail>
                <Detail label="Total">
                  <span className="font-semibold tabular-nums">
                    {formatMoney(invoice.totalCents, invoice.currency)}
                  </span>
                </Detail>
                {invoice.sentAt ? (
                  <Detail label="Sent">{formatDateTime(invoice.sentAt)}</Detail>
                ) : null}
                {invoice.paidAt ? (
                  <Detail label="Paid">{formatDateTime(invoice.paidAt)}</Detail>
                ) : null}
                {record ? (
                  <Detail label="From">
                    <Link
                      href={edit_app_list_record_path(app.id, record.listKey, record.id)}
                      className="font-medium underline"
                    >
                      {record.title}
                    </Link>
                  </Detail>
                ) : null}
                {invoice.memo ? (
                  <Detail label="Memo" className="col-span-full">
                    <span className="whitespace-pre-line">{invoice.memo}</span>
                  </Detail>
                ) : null}
              </dl>

              {invoice.lines.length === 0 ? (
                <p className="text-muted-foreground text-sm">This invoice has no lines.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">Unit price</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoice.lines.map((line, index) => (
                      <TableRow key={line.id ?? index}>
                        <TableCell className="min-w-40 whitespace-normal">
                          {line.description}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{line.quantity}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney(line.unitAmountCents, invoice.currency)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney(line.quantity * line.unitAmountCents, invoice.currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={3}>Total</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney(invoice.totalCents, invoice.currency)}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                <h2>Messages</h2>
              </CardTitle>
              {mailMode === 'demo' ? (
                <p className="text-muted-foreground text-sm">
                  Demo mode: email is recorded here but not sent.
                </p>
              ) : null}
            </CardHeader>
            <CardContent>
              {messages.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  Nothing has been sent for this invoice yet.
                </p>
              ) : (
                <MessageTable
                  appId={app.id}
                  messages={messages}
                  showSubject={false}
                  showInvoice={false}
                />
              )}
            </CardContent>
          </Card>
        </div>

        <div className="min-w-0 space-y-6">
          {canSend ? (
            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>Send</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {invoice.customer ? (
                  <p className="text-muted-foreground break-words">
                    Emails the invoice to {invoice.customer.email}.
                  </p>
                ) : null}

                {paymentConnection ? null : (
                  <p className="text-muted-foreground">
                    No payment account is connected, so invoices go out without a pay link.{' '}
                    <Link
                      href={app_connections_path(app.id)}
                      className="text-foreground font-medium underline"
                    >
                      Connections
                    </Link>
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  <ConfirmAction
                    title={
                      invoice.sentAt
                        ? 'Send this invoice again?'
                        : 'Send this invoice to the customer?'
                    }
                    description={
                      invoice.sentAt ? emailNote : `${emailNote} The invoice gets its number.`
                    }
                    confirmLabel={sendLabel}
                    onConfirm={() =>
                      router.post(
                        app_invoice_delivery_path(app.id, invoice.id),
                        {},
                        {
                          preserveScroll: true,
                          onStart: () => setSending(true),
                          onFinish: () => setSending(false),
                        }
                      )
                    }
                  >
                    <Button type="button" disabled={sending}>
                      {sendLabel}
                    </Button>
                  </ConfirmAction>

                  {invoice.status === 'open' ? (
                    <ConfirmAction
                      title="Void this invoice?"
                      description="The customer can no longer pay it. A voided invoice cannot be reopened."
                      confirmLabel="Void"
                      onConfirm={() =>
                        router.post(
                          app_invoice_voidance_path(app.id, invoice.id),
                          {},
                          { preserveScroll: true }
                        )
                      }
                    >
                      <Button type="button" variant="outline">
                        Void
                      </Button>
                    </ConfirmAction>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ) : null}

          {invoice.payPageUrl ? (
            <PayLinkCard url={invoice.payPageUrl} demo={invoice.demoPayment} />
          ) : null}

          <BooksCard
            appId={app.id}
            invoice={invoice}
            booksConnection={booksConnection}
            quickbooksIncluded={app.features.quickbooks}
          />
        </div>
      </div>
    </div>
  );
};

export default InvoicesShow;
